import { List, Map, Seq, fromJS } from 'immutable';
import { createSelector } from 'reselect';

import { createImmutableSelector } from 'flow/selectors/selector-utils';

import { MAX_ATTACHMENT_SIZE_DEFAULT } from 'flow/components/tickets/Input/InputActions';
import { ActiveLineData } from 'flow/types/ActiveLineData';
import { QueueKey, TagKey, TicketKey } from 'flow/types/EntityKeys';
import { State } from 'flow/types/State';
import { Tag } from 'flow/types/Tag';
import {
    DEFAULT_TICKET_VISIBILITY,
    DEFAULT_TICKETS_SORT,
    TicketMessageChannel,
    TicketReplyType,
    SortingData,
    TicketSortDirection,
    TicketSortField,
    isTicketVisibility,
    TicketVisibility,
    TicketVisibilityForDisplay,
} from 'flow/types/Tickets';
import { TypedMap } from 'flow/types/TypedMap';
import {
    getDataFromBagInTopMessage,
    makeTicketsKeysDataComparator,
    TICKET_KEYS_DATA_PATHS,
    TICKETS_LIST_TABS_KEYS,
    TICKETS_SETTINGS_KEYS,
    TICKETS_SETTINGS_PROPERTY_NAMES,
} from 'flow/utils/ticket-utils';
import {
    selectOwnExtensionKey,
    selectOwnUserHasCapability,
} from './ExtensionSelectors';
import { isKnownTicketOrigin } from 'flow/utils/ticket-channel-utils';
import { selectWaitingQueueItems } from './queue-selectors';
import { getQueueNameForQueue } from './selectors';
import { selectEvents, selectHasFetchedEvents } from './TicketEventSelectors';

const MAP_EMPTY = Map();

const DEFAULT_AUTO_CREATION_SETTING = TICKETS_SETTINGS_KEYS.AUTO_CREATION.NEVER;
const DEFAULT_AUTO_CLOSE_ON_MISSED_CALLS_SETTING = false;
const DEFAULT_AUTO_CLOSE_ON_HANGUP_SETTING = false;

const DEFAULT_TICKETS_SETTINGS = fromJS({
    [TICKETS_SETTINGS_PROPERTY_NAMES.AUTO_CREATION]:
        DEFAULT_AUTO_CREATION_SETTING,
    [TICKETS_SETTINGS_PROPERTY_NAMES.AUTO_CLOSE_ON_MISSED_CALLS]:
        DEFAULT_AUTO_CLOSE_ON_MISSED_CALLS_SETTING,
    [TICKETS_SETTINGS_PROPERTY_NAMES.AUTO_CLOSE_ON_HANGUP]:
        DEFAULT_AUTO_CLOSE_ON_HANGUP_SETTING,
});

const DEFAULT_TICKETS_SORT_MAP = fromJS(DEFAULT_TICKETS_SORT);

export const selectTicketsSort = (state: State) =>
    state.tickets.getIn(['settings', 'sort'], DEFAULT_TICKETS_SORT_MAP);

export const selectTicketsSortField = (state: State): TicketSortField =>
    selectTicketsSort(state).get('field');

export const selectTicketsSortDirection = (state: State): TicketSortDirection =>
    selectTicketsSort(state).get('direction');

// Maps the webclient sort field to the omni-ticket-service sort modes.
const SORT_FIELD_API_VALUES: Record<TicketSortField, string> = {
    createdDate: 'CREATED',
    lastUpdate: 'LAST_UPDATED',
};

// API-formatted sort params for the generic tickets endpoint, shared by the
// tab list fetches and the search action so both order results identically.
export const selectTicketsSortForApi = (state: State) => ({
    sortField: SORT_FIELD_API_VALUES[selectTicketsSortField(state)],
    sortDirection: selectTicketsSortDirection(state)?.toUpperCase(),
});

export const selectTicketsTabIsEnforced = (state: State) =>
    state.tickets.getIn(['settings', 'ticketsTabIsEnforced'], false);

export const selectShouldSeeTicketsTab = (state: State) =>
    selectTicketsTabIsEnforced(state);

export const selectEmailSignaturesEnabled = (state: State) =>
    state.tickets.getIn(['settings', 'emailSignaturesEnabled'], false);

export const selectTicket = (state: State, key: TicketKey) =>
    state.tickets.getIn(['ticketsData', 'byKey', key]) ||
    state.tickets.getIn(['tempSearchData', 'byKey', key]);

export const selectTicketIsFetched = (state: State, key: TicketKey) =>
    !!selectTicket(state, key);

const selectAllTicketsInStore = (state: State) =>
    state.tickets.getIn(['ticketsData', 'byKey']);

export const selectUserIsMemberOfSomeTicket = (state: State) => {
    return !!selectAllTicketsInStore(state); //equivalent to getAtLeastOneTicketHasBeenCreated
};

export const selectAssignee = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('assignee') || 'unassigned';

export const selectTicketState = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('state');

export const selectMembers = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('members');

export const selectMembersCount = (state: State, key: TicketKey) =>
    selectMembers(state, key)?.size;

export const selectTicketCreatedDate = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('createdDate');

const selectTicketLastUpdate = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('lastUpdate');

// The timestamp on the active sort field, so the list item shows the value the
// list is ordered by (lastUpdate vs createdDate) rather than a fixed field.
export const selectTicketSortedTimestamp = (state: State, key: TicketKey) =>
    selectTicketsSortField(state) === 'lastUpdate'
        ? selectTicketLastUpdate(state, key)
        : selectTicketCreatedDate(state, key);

const selectOriginQueueKey = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'key']);

export const selectNameOfOriginQueue = (state: State, key: TicketKey) => {
    const queueKey = selectOriginQueueKey(state, key);
    if (!queueKey) return undefined;
    return getQueueNameForQueue(state, queueKey);
};

export const selectSortingDataOpenAssignedToLoggedInUser = (state: State) =>
    state.tickets.getIn(['openTicketsAssignedToUserKeysData', 'keys']);

const selectSortingDataAllStates = (state: State) =>
    state.tickets.getIn(['ticketsAllKeysData', 'keys']);

const selectTicketsSearched = (state: State) =>
    state.tickets.getIn(['tempSearchData', 'byKey']);

const SEQ_EMPTY: Seq.Indexed<TicketKey> = Seq.Indexed();

export const selectTicketKeysAllStatesSorted = createSelector(
    [
        selectSortingDataAllStates,
        selectTicketsSortField,
        selectTicketsSortDirection,
    ],
    (data, sortField, sortDirection) => {
        if (!data) return SEQ_EMPTY;
        return data
            .sort(makeTicketsKeysDataComparator(sortField, sortDirection))
            .keySeq();
    },
);

const selectOriginLocutusSessionId = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'locutusSessionId']);

// The reverse indexes below never overwrite an existing entry, so when two
// items carry the same match value the first one in iteration order wins —
// the same item the pre-index .find() implementation returned.
const indexMatchValue = (
    index: Map<string, string>,
    matchValue: string,
    itemKey: string,
) => {
    if (!index.has(matchValue)) index.set(matchValue, itemKey);
};

// A ticket matches its queue entry on the origin's locutus session id (call
// tickets) or on the ticket-key form of the bag ticket id (non-call tickets).
const lookupTicketInMatchValueIndex = (
    state: State,
    index: Map<string, string>,
    ticketKey: TicketKey,
) => {
    const locutusSessionId = selectOriginLocutusSessionId(state, ticketKey);
    return (
        (locutusSessionId && index.get(locutusSessionId)) ||
        index.get(ticketKey)
    );
};

// Reverse index over the waiting-queue items, rebuilt only when they change,
// so every mounted ticket list item looks up its queue entry in O(1) instead
// of scanning all items.
const selectWaitingQueueItemKeysByMatchValue = createSelector(
    [selectWaitingQueueItems],
    (waitingQueueItems) =>
        Map<string, string>().withMutations((index) => {
            waitingQueueItems?.forEach((item) => {
                const itemKey = item.get('key');
                const locutusSessionId = item.get('locutusSessionId');
                if (locutusSessionId) {
                    indexMatchValue(index, locutusSessionId, itemKey);
                }
                const telavoxOmniTicketId = item.get('telavoxOmniTicketId');
                if (telavoxOmniTicketId) {
                    indexMatchValue(
                        index,
                        `ticket-${telavoxOmniTicketId}`,
                        itemKey,
                    );
                }
            });
        }),
);

export const selectWaitingQueueItemKeyForTicket = (
    state: State,
    ticketKey: TicketKey,
) =>
    lookupTicketInMatchValueIndex(
        state,
        selectWaitingQueueItemKeysByMatchValue(state),
        ticketKey,
    );

const selectQueueCallsByChannel = (state: State) =>
    state.pbx.get('queueCallsByChannel');

const ACTIVE_CALL_KEY_PREFIX = 'activecall-';
const CALL_RECORD_KEY_PREFIX = 'call-';

// A ticket is keyed on the caller's session id, while a channel of the call
// carries the agent's, so the two never match directly. queueCallsByChannel
// remembers the caller's call record against the channel, which bridges them.
// A channel can sit under two queues once a call is transferred, so every
// entry is collected.
const getCallerSessionIds = (
    queueCallsByChannel: any,
    channelId: string,
): string[] => {
    const queueCalls = channelId && queueCallsByChannel?.get(channelId);
    if (!queueCalls) return [];

    const callerSessionIds: string[] = [];
    queueCalls.forEach((queueCall: any) => {
        const callRecordKey = queueCall?.get('callRecordKey');
        // The id is a UUID, so it is sliced off the prefix. Splitting on the
        // dash would keep only its first block.
        if (callRecordKey)
            callerSessionIds.push(
                callRecordKey.slice(CALL_RECORD_KEY_PREFIX.length),
            );
    });
    return callerSessionIds;
};

const selectTopActiveQueueCallsByQueue = (state: State) =>
    state.pbx.getIn(['topActiveQueueCalls', 'byQueue']);

const selectUniqueIdToChannel = (state: State) =>
    state.top.get('uniqueIdToChannel');

// The index below rebuilds whenever any TOP channel changes (uniqueIdToChannel
// is merged into on every channel event), so the per-channel extraction —
// toObject() plus base64 bag decoding — is cached against the channel record's
// identity: unchanged channels skip straight to the cached values. The cache
// is only correct while the extraction depends on nothing but the channel
// record; a second input would have to become part of the key.
type ChannelMatchValues = {
    locutusSessionId: string | undefined;
    ticketId: string | undefined;
};

const channelMatchValues = new WeakMap<
    Map<string, string>,
    ChannelMatchValues
>();

const getChannelMatchValues = (
    channel: Map<string, string>,
): ChannelMatchValues => {
    let matchValues = channelMatchValues.get(channel);
    if (!matchValues) {
        matchValues = {
            locutusSessionId: channel.get('LocutusSessionId'),
            ticketId: getDataFromBagInTopMessage(
                channel.toObject(),
                'telavox.omni.ticket_id',
            ),
        };
        channelMatchValues.set(channel, matchValues);
    }
    return matchValues;
};

// Indexes one answered queue call under every value a ticket can match it on.
const indexOngoingQueueCall = (
    index: Map<string, string>,
    uniqueId: string,
    channel: Map<string, string>,
    queueCallsByChannel: any,
) => {
    const { locutusSessionId, ticketId } = getChannelMatchValues(channel);
    if (locutusSessionId) {
        indexMatchValue(index, locutusSessionId, uniqueId);
    }
    if (ticketId) {
        indexMatchValue(index, `ticket-${ticketId}`, uniqueId);
    }
    // The channel's own session is the agent's, so a ticket whose bag ticket
    // id never arrived is only reachable through the caller's session. This
    // lookup stays out of getChannelMatchValues so its cache key remains the
    // channel record alone.
    getCallerSessionIds(queueCallsByChannel, uniqueId).forEach(
        (callerSessionId) => indexMatchValue(index, callerSessionId, uniqueId),
    );
};

// Reverse index over answered ('Up') queue calls, so a call ticket finds the
// ongoing call handling it in O(1) — same pattern and match values as the
// waiting-queue index above.
const selectOngoingQueueCallKeysByMatchValue = createSelector(
    [
        selectTopActiveQueueCallsByQueue,
        selectUniqueIdToChannel,
        selectQueueCallsByChannel,
    ],
    (callStatesByQueue, uniqueIdToChannel, queueCallsByChannel) =>
        Map<string, string>().withMutations((index) => {
            callStatesByQueue?.forEach((callStates) => {
                callStates.forEach((callState: string, uniqueId: string) => {
                    if (callState !== 'Up') return;
                    const channel = uniqueIdToChannel?.get(uniqueId);
                    if (!channel) return;
                    indexOngoingQueueCall(
                        index,
                        uniqueId,
                        channel,
                        queueCallsByChannel,
                    );
                });
            });
        }),
);

export const selectOngoingQueueCallKeyForTicket = (
    state: State,
    ticketKey: TicketKey,
) =>
    lookupTicketInMatchValueIndex(
        state,
        selectOngoingQueueCallKeysByMatchValue(state),
        ticketKey,
    );

export const selectKeysTicketsSearched = createSelector(
    [selectTicketsSearched],
    (tickets) => {
        if (!tickets) return List<TicketKey>();
        return tickets.keySeq();
    },
);

const selectTicketsBadgeCount = (state: State) =>
    state.tickets.getIn(['ticketsData', 'ticketsBadgeCount'], 0);

const selectNumberOfTicketsOpenAssignedToLoggedInUser = (state: State) =>
    state.tickets.getIn(
        ['ticketsData', 'totalNumberOfTicketsOpenAssignedToUser'],
        0,
    );

const selectOwnActiveLineData = (state: State) =>
    state.extensions.getIn(['activeLineData', selectOwnExtensionKey(state)]);

const selectTicketKeysByLocutusSessionId = (state: State) =>
    state.tickets.get('ticketKeysByLocutusSessionId');

const findTicketKeyForLeg = (
    legKey: string,
    queueCallsByChannel: any,
    ticketKeysByLocutusSessionId: any,
) =>
    getCallerSessionIds(
        queueCallsByChannel,
        legKey?.slice(ACTIVE_CALL_KEY_PREFIX.length),
    ).reduce(
        (found: any, callerSessionId) =>
            found || ticketKeysByLocutusSessionId?.get(callerSessionId),
        undefined,
    );

export const selectOwnSessionsRingingByTicketKey = createSelector(
    [
        selectOwnActiveLineData,
        selectTicketKeysByLocutusSessionId,
        selectQueueCallsByChannel,
    ],
    (ownActiveSessions, ticketKeysByLocutusSessionId, queueCallsByChannel) => {
        return (
            ownActiveSessions?.reduce(
                (acc: any, item: TypedMap<ActiveLineData>) => {
                    const ticketId = item.getIn([
                        'bagVariables',
                        'telavox.omni.ticket_id',
                    ]);
                    // The routing script can set telavox.omni.ticket_id after
                    // the leg already rings. The leg then never carries it, so
                    // fall back to the ticket the ticket-created push reported
                    // for this leg's queue call.
                    const ticketKey = ticketId
                        ? `ticket-${ticketId}`
                        : findTicketKeyForLeg(
                              item.get('key'),
                              queueCallsByChannel,
                              ticketKeysByLocutusSessionId,
                          );
                    if (
                        !ticketKey ||
                        item.get('state')?.toLowerCase() !== 'ringing'
                    )
                        return acc;

                    // The leg's key, not the leg itself: a leg record's
                    // identity changes on every call event, which would stop
                    // consumers comparing this map by value.
                    return acc.set(ticketKey, item.get('key'));
                },
                Map(),
            ) || Map()
        );
    },
);

const selectNumberOfRingingSessionsWithTickets = createSelector(
    [selectOwnSessionsRingingByTicketKey],
    (sessions) => sessions?.size || 0,
);

// A ringing ticket assigned to the logged-in user is already in the assigned
// total, and the Mine list pins it once instead of listing it twice. Count
// only the ringing tickets that total does not hold, so the badge agrees with
// the number of rows.
const selectNumberOfRingingSessionsWithUnassignedTickets = createSelector(
    [
        selectOwnSessionsRingingByTicketKey,
        selectSortingDataOpenAssignedToLoggedInUser,
    ],
    (sessions, sortingDataOpenAssignedToLoggedInUser) =>
        sessions?.reduce(
            (count: number, _legKey: any, ticketKey: TicketKey) =>
                sortingDataOpenAssignedToLoggedInUser?.has(ticketKey)
                    ? count
                    : count + 1,
            0,
        ) || 0,
);

export const selectTicketsSearchedNextPageNumberForAPIRequest = (
    state: State,
) => {
    return state.tickets.getIn(['tempSearchData', 'nextPageNumber']) || 1;
};

export const selectApiHasMoreTicketsSearchedData = (state: State) => {
    return state.tickets.getIn(['tempSearchData', 'apiHasMoreData']) !== false;
};

export const selectNumberOfItemsForMineTab = createSelector(
    [
        selectNumberOfTicketsOpenAssignedToLoggedInUser,
        selectNumberOfRingingSessionsWithUnassignedTickets,
        selectShouldSeeTicketsTab,
    ],
    (
        numberOfTicketsOpenAssignedToLoggedInUser,
        numberOfSipSessionsWithTickets,
        shouldSeeTicketsTab,
    ) => {
        if (!shouldSeeTicketsTab) return 0;
        return (
            numberOfSipSessionsWithTickets +
            numberOfTicketsOpenAssignedToLoggedInUser
        );
    },
);

export const selectNumberOfItemsForBadgeInAgentView = createSelector(
    [
        selectTicketsBadgeCount,
        selectNumberOfRingingSessionsWithTickets,
        selectShouldSeeTicketsTab,
    ],
    (
        ticketsBadgeCount,
        sessionsRingingWithTicketsCount,
        shouldSeeTicketsTab,
    ) => {
        if (!shouldSeeTicketsTab) return 0;
        return sessionsRingingWithTicketsCount + ticketsBadgeCount;
    },
);

export const selectOpenTicketsAssignedToLoggedInUserRequestPageNumber = (
    state: State,
) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
            'nextPageNumber',
        ]) || 1
    );
};

export const selectApiHasMoreOpenTicketsAssignedToLoggedInUserData = (
    state: State,
) =>
    state.tickets.getIn([
        TICKET_KEYS_DATA_PATHS.ASSIGNED_LOGGED_IN_USER,
        'apiHasMoreData',
    ]) !== false;

export const selectOpenTicketsUnassignedRequestPageNumber = (state: State) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.UNASSIGNED,
            'nextPageNumber',
        ]) || 1
    );
};

export const selectApiHasMoreTicketsOpenUnassignedData = (state: State) =>
    state.tickets.getIn(['openTicketsUnassignedKeysData', 'apiHasMoreData']) !==
    false;

export const selectTicketsAllStatesRequestPageNumber = (state: State) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.ALL_LIST,
            'nextPageNumber',
        ]) || 1
    );
};

export const selectApiHasMoreTicketsAllStatesData = (state: State) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.ALL_LIST,
            'apiHasMoreData',
        ]) !== false
    );
};

export const selectClosedTicketsRequestPageNumber = (state: State) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.CLOSED,
            'nextPageNumber',
        ]) || 1
    );
};

export const selectApiHasMoreClosedTicketsData = (state: State) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.CLOSED,
            'apiHasMoreData',
        ]) !== false
    );
};

export const selectRelatedTicketsRequestPageNumber = (
    state: State,
    key: TicketKey,
) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.RELATED,
            key,
            'nextPageNumber',
        ]) || 1
    );
};

export const selectApiHasMoreRelatedTicketsData = (
    state: State,
    key: TicketKey,
) => {
    return (
        state.tickets.getIn([
            TICKET_KEYS_DATA_PATHS.RELATED,
            key,
            'apiHasMoreData',
        ]) !== false
    );
};

export const selectTagsTicketIsTaggedWith = (
    state: State,
    ticketKey: TicketKey,
) =>
    state.tickets.getIn(
        ['metadata', 'perTicket', ticketKey, 'tagsData'],
        MAP_EMPTY,
    );

export const selectAllTagsAvailableForTicketTagging = (state: State) =>
    state.tickets.getIn(['metadata', 'allTags'], MAP_EMPTY);

export const selectTagFromAllTagsWithLabel = (state: State, label: string) =>
    selectAllTagsAvailableForTicketTagging(state)?.find(
        (tag: TypedMap<Tag>) => tag.get('label') === label,
    );

export const selectTagExistsInAvailableTags = (
    state: State,
    tagKey?: TagKey,
    label?: string,
) =>
    selectAllTagsAvailableForTicketTagging(state)?.has(tagKey) ||
    !!selectTagFromAllTagsWithLabel(state, label);

/**
 * Absent on tickets fetched before the backend started sending the field, so it
 * falls back to the restrictive value rather than leaking a wider access state.
 */
export const selectTicketVisibility = (
    state: State,
    key: TicketKey,
): TicketVisibilityForDisplay => {
    const visibility = selectTicket(state, key)?.get('visibility');

    // An absent value means the ticket was fetched before the backend sent the
    // field, and private is the backend's own default — the safe read. A value that
    // is present but unknown is a tier this client cannot name, and calling that
    // private would understate the access.
    if (!visibility) return 'private';

    return isTicketVisibility(visibility) ? visibility : 'unknown';
};

export const selectNonMembersAreMentionable = (
    state: State,
    key: TicketKey,
) => {
    const visibility = selectTicketVisibility(state, key);
    return visibility === 'restricted' || visibility === 'public';
};

export const selectOwnUserIsMemberOfTicket = (
    state: State,
    ticketKey: TicketKey,
) =>
    selectTicket(state, ticketKey)
        ?.get('members')
        ?.includes(selectOwnExtensionKey(state));

export const selectTotalNumberOfRelatedTickets = (
    state: State,
    key: TicketKey,
) => state.tickets.getIn(['ticketsData', 'totalNumberOfRelatedTickets', key]);

export const selectTicketsListIsMounted = (state: State) =>
    state.tickets.getIn(['componentsMountStatus', 'ticketsList', 'isMounted']);

export const selectTicketsListTabSelected = (state: State) =>
    state.tickets.getIn([
        'componentsMountStatus',
        'ticketsList',
        'tabSelected',
    ]);

export const selectTicketViewIsMounted = (state: State, key: TicketKey) =>
    state.tickets.hasIn(['componentsMountStatus', 'ticketView', key]);

export const selectTicketEventsIsMounted = (state: State, key: TicketKey) =>
    state.tickets.hasIn(['componentsMountStatus', 'ticketEvents', key]);

/**
 * Whether the ticket may be on screen: a ticket list is mounted, or it has a
 * ticket view of its own — the ticket page, the overview popover or the PBX
 * detail page tab.
 *
 * The list arm is deliberately coarse. Whether this ticket is one of its rows
 * depends on the tab, the sort and how far the user has paged, so it errs
 * towards saying yes and costs a request that answers nothing.
 */
export const selectTicketIsOnScreen = (state: State, key: TicketKey) =>
    !!selectTicketsListIsMounted(state) ||
    selectTicketViewIsMounted(state, key);

export const selectTicketViewsMountedData = (state: State) =>
    state.tickets.getIn(['componentsMountStatus', 'ticketView']);

export const selectTicketPageMounted = createSelector(
    [selectTicketViewsMountedData],
    (ticketViewsMountedData) =>
        ticketViewsMountedData
            ?.find((mountStatus) => mountStatus.get('context') === 'page')
            ?.get('ticketKey'),
);

export const selectOwnUserHasCapabilityTickets = (state: State) =>
    selectOwnUserHasCapability(state, 'tickets');

export const selectVisitorContact = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('visitorDetails');

export const selectOriginVisitorPhoneNumber = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'visitorId', 'number']);

export const selectOriginVisitorId = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'visitorId']);

export const selectVisitorDisplayName = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'visitorDisplayName']);

export const selectVisitorContactKey = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['visitorDetails', 'key']);

export const selectOriginDTOSubtype = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.getIn(['origin', 'DTOSubtype']);

export const selectTicketOriginIsKnown = (state: State, key: TicketKey) =>
    isKnownTicketOrigin(selectOriginDTOSubtype(state, key));

// Whether a WhatsApp ticket's events are available. Non-WhatsApp tickets have
// nothing to wait for, so this stays a constant true for them — keeping the
// composer's loading state (disabled input + skeleton placeholder) scoped to
// WhatsApp and adding no re-renders elsewhere.
export const selectWhatsAppEventsAreFetched = (state: State, key: TicketKey) =>
    selectOriginDTOSubtype(state, key) !== 'whatsapp' ||
    selectHasFetchedEvents(state, key);

export const selectSubject = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('subject') || '';

export const selectShowSetAwaitingActivityItem = (
    state: State,
    key: TicketKey,
) => {
    const assignee = selectAssignee(state, key);
    return (
        selectTicketState(state, key) === 'open' &&
        (assignee == selectOwnExtensionKey(state) || assignee === 'unassigned')
    );
};

export const selectAwaitingActivity = (state: State, key: TicketKey) =>
    selectTicket(state, key)?.get('awaitingActivity');

export const selectTicketsAllowedMimeTypesData = (
    state: State,
    channel: TicketMessageChannel,
) =>
    state.tickets.getIn([
        'settings',
        'attachments',
        channel,
        'allowedMimetypes',
    ]);

export const selectTicketsBlockedMimeTypes = (
    state: State,
    channel: TicketMessageChannel,
) =>
    state.tickets.getIn([
        'settings',
        'attachments',
        channel,
        'blockedMimetypes',
    ]);

export const selectTicketAttachmentMaxSize = (
    state: State,
    channel: 'sms' | 'email' | 'whatsapp' | 'internal',
    mimeType: string,
) => {
    if (channel === 'internal') return MAX_ATTACHMENT_SIZE_DEFAULT; // 20 MB for internal notes
    return selectTicketsAllowedMimeTypesData(state, channel)?.getIn([
        mimeType,
        'maxSizeBytes',
    ]);
};

export const selectHasAttachmentRestrictionsInStore = (state: State) =>
    state.tickets.hasIn(['settings', 'attachments']);

export const selectQueueIsEligibleForTicketsConfiguration = (
    state: State,
    queueEntityKey: QueueKey,
) =>
    state.pbx.getIn([
        'queues',
        queueEntityKey,
        'isEligibleForTicketsConfiguration',
    ]);

export const selectTicketReplyDraft = (
    state: State,
    ticketKey: TicketKey,
    replyType: TicketReplyType,
) => state.tickets.getIn(['drafts', ticketKey, replyType, 'text'], '');

export const selectTicketReplyDraftMentions = (
    state: State,
    ticketKey: TicketKey,
    replyType: TicketReplyType,
) => state.tickets.getIn(['drafts', ticketKey, replyType, 'mentions']);

export const selectTicketsSettingsForQueue = (
    state: State,
    queueEntityKey: QueueKey,
) =>
    state.tickets.getIn(
        ['settings', 'perQueue', queueEntityKey],
        DEFAULT_TICKETS_SETTINGS,
    );

export const selectDefaultTicketVisibilityForQueue = (
    state: State,
    queueEntityKey: QueueKey,
): TicketVisibility =>
    state.tickets.getIn(
        ['settings', 'defaultVisibilityPerQueue', queueEntityKey, 'visibility'],
        DEFAULT_TICKET_VISIBILITY,
    );

const selectKeysDataOpenUnassigned = (state: State) =>
    state.tickets.getIn([TICKET_KEYS_DATA_PATHS.UNASSIGNED, 'keys']);

// Sorted at read time (like the All and Mine lists) so TOP updates to
// sort-relevant fields reorder the list without a refetch — the reducer only
// updates keys-data values in place.
export const selectKeysForTicketsOpenUnassigned = createSelector(
    [
        selectKeysDataOpenUnassigned,
        selectTicketsSortField,
        selectTicketsSortDirection,
    ],
    (keysData, sortField, sortDirection): Seq.Indexed<TicketKey> => {
        if (!keysData) return Seq.Indexed();

        return keysData
            .sort(makeTicketsKeysDataComparator(sortField, sortDirection))
            .keySeq();
    },
);

/** What a tab's keys-data map holds: the fields the list is ordered by. */
type TicketsKeysData = Map<TicketKey, TypedMap<SortingData>>;

/**
 * Display order of the Mine tab: tickets with a ringing SIP session pinned to
 * the top, the rest by the globally selected sort.
 *
 * Compared by value: the ringing-sessions map is rebuilt whenever any queue
 * channel changes, so comparing by reference would re-sort every assigned
 * ticket on each of those events.
 */
export const selectTicketKeysMineSorted = createImmutableSelector(
    [
        selectSortingDataOpenAssignedToLoggedInUser,
        selectOwnSessionsRingingByTicketKey,
        selectTicketsSortField,
        selectTicketsSortDirection,
    ],
    (
        sortingDataOpenAssignedToLoggedInUser,
        ringingSipSessionsWithTickets,
        sortField,
        sortDirection,
    ) => {
        const sorter = makeTicketsKeysDataComparator(sortField, sortDirection);
        const keysWithActiveSipSessions =
            ringingSipSessionsWithTickets.keySeq();
        const keysWithNoActiveSipSessions = keysWithActiveSipSessions
            .reduce(
                (acc: TicketsKeysData, key: TicketKey) => acc.remove(key),
                sortingDataOpenAssignedToLoggedInUser ||
                    Map<TicketKey, TypedMap<SortingData>>(),
            )
            .sort(sorter)
            .keySeq();

        return keysWithActiveSipSessions?.concat(keysWithNoActiveSipSessions);
    },
);

export const selectTicketsRelatedSortingData = (
    state: State,
    ticketKey: TicketKey,
) => state.tickets.getIn([TICKET_KEYS_DATA_PATHS.RELATED, ticketKey, 'keys']);

/**
 * The mounted tab's keys in the order it shows them, so that a caller stepping
 * to the next ticket lands on the one below the current row. Each arm is the
 * selector its list renders from: an order derived a second time here would
 * drift from the visible one, which is what it did until the sorted Mine and
 * All selectors replaced their unsorted namesakes.
 */
export const selectTicketsKeysForTabMounted = (state: State) => {
    const tabSelected = selectTicketsListTabSelected(state);

    if (!tabSelected) return undefined;

    if (tabSelected === TICKETS_LIST_TABS_KEYS.MY_TICKETS) {
        return selectTicketKeysMineSorted(state);
    } else if (tabSelected === TICKETS_LIST_TABS_KEYS.UNASSIGNED) {
        return selectKeysForTicketsOpenUnassigned(state);
    } else if (tabSelected === TICKETS_LIST_TABS_KEYS.ALL) {
        return selectTicketKeysAllStatesSorted(state);
    }
    return undefined;
};

const WHATSAPP_INACTIVITY_THRESHOLD_MS = 86400000;
export const makeSelectRequiresWhatsAppTemplateReply = () =>
    createSelector(
        [selectOriginDTOSubtype, selectEvents],
        (originSubtype, events) => {
            if (originSubtype !== 'whatsapp') return false;
            const mostRecentIncomingWhatsAppMessageDate = events
                ?.filter(
                    (event) =>
                        event.get('DTOSubtype') ===
                            'OmniWhatsAppMessageTicketEvent' &&
                        event.get('direction') === 'INCOMING',
                )
                ?.maxBy((event) => event.get('timeStamp'))
                ?.get('timeStamp');

            if (!mostRecentIncomingWhatsAppMessageDate) return false;

            const dateTwentyFourHoursAgoMs =
                Date.now() - WHATSAPP_INACTIVITY_THRESHOLD_MS;
            const dateWhatsAppMessageMs = new Date(
                mostRecentIncomingWhatsAppMessageDate,
            )?.getTime();

            return dateTwentyFourHoursAgoMs > dateWhatsAppMessageMs;
        },
    );
